import {
  Button,
  CustomFilter,
  DatePicker,
  Dropdown,
  EllipsisSpan,
  FieldTable,
  Form,
  Input,
  Modal,
  Paging,
  Radio,
  Select,
  Tag,
  Tabs,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
} from '@maxtropy/components';
import {
  apiV2DataMonitorAlarmRecordBatchDeletePost,
  apiV2DataMonitorAlarmRecordBatchRelievePost,
  apiV2DataMonitorAlarmRecordAlarmLevelCountPost,
  apiV2DataMonitorAlarmRecordDeviceLabelListPost,
  apiV2DataMonitorAlarmRecordExportGet,
  apiV2DataMonitorAlarmRecordPagePost,
  V2DataMonitorAlarmRecordAlarmLevelCountPostResponse,
  V2DataMonitorAlarmRecordPagePostRequest,
  V2DataMonitorAlarmRecordPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { Cascader, InputNumber, Space } from 'antd';
import { DefaultOptionType } from 'antd/es/cascader';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { getDeviceTypeTree } from '@/api/deviceType';
import { getOuListOwned } from '@/api/ou';
import { PermissionsType } from '@/common/permissionsConst';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { AlarmLevel, AlarmLevelColorDisplay, AlarmLevelDisplay } from '@/shared/types';
import { useHasPermission } from '@/utils/utils';
import styles from './index.module.scss';

import {
  DataMonitoringRuleType,
  DataMonitoringRuleTypeDisplay,
  dataMonitoringRuleTypeOptions,
} from '../DataMonitoringAlarmRules/constants';

const { RangePicker } = DatePicker;

const dateFormat = 'YYYY/MM/DD HH:mm';
const displayDateFormat = 'YYYY/MM/DD HH:mm';

const alarmStateTabs = [
  { key: '1', label: '未恢复报警' },
  { key: '0', label: '已恢复报警' },
];

const alarmLevelItems = [
  { label: '全部', value: 5, countKey: 'total' as const },
  { label: '最高级', value: AlarmLevel.HIGHEST, countKey: 'topLevel' as const },
  { label: '高级', value: AlarmLevel.HIGH, countKey: 'highLevel' as const },
  { label: '中级', value: AlarmLevel.MIDDLE, countKey: 'middleLevel' as const },
  { label: '低级', value: AlarmLevel.LOW, countKey: 'lowLevel' as const },
  { label: '最低级', value: AlarmLevel.LOWEST, countKey: 'lowestLevel' as const },
];

const durationConditionOptions = [
  { label: '>=', value: '4' },
  { label: '>', value: '1' },
  { label: '<', value: '2' },
  { label: '<=', value: '5' },
  { label: '=', value: '3' },
];

const durationUnitOptions = [
  { label: '分钟', value: '1' },
  { label: '小时', value: '2' },
  { label: '天', value: '3' },
];

const getAlarmRecordDeviceLabelList = () =>
  apiV2DataMonitorAlarmRecordDeviceLabelListPost({}).then(response => response.list ?? []);

interface FilterParams {
  ruleCodeOrName?: string;
  alarmTimeRange?: [Dayjs, Dayjs];
  ouIds?: number[];
  ruleType?: DataMonitoringRuleType;
  deviceCodeOrName?: string;
  deviceTypeIds?: number[][];
  durationFilter?: string;
  duration?: number;
  durationUnit?: string;
  deviceLabelIds?: number[];
}

type DataMonitoringAlarmSearchParams = Omit<
  V2DataMonitorAlarmRecordPagePostRequest,
  'timestamp' | 'page' | 'size' | 'state' | 'levels'
>;

type DataMonitoringAlarmRecord = NonNullable<V2DataMonitorAlarmRecordPagePostResponse['list']>[number];

const DataMonitoringAlarmRecords: React.FC = () => {
  const [form] = Form.useForm<FilterParams>();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const { data: ouList = [] } = useRequest(getOuListOwned);
  const { data: deviceTypeData } = useRequest(getDeviceTypeTree);
  const { data: deviceLabelList = [] } = useRequest(getAlarmRecordDeviceLabelList);

  const [tabKey, setTabKey] = useState('1');
  const [level, setLevel] = useState(5);
  const [searchParams, setSearchParams] = useState<DataMonitoringAlarmSearchParams>({});
  const [records, setRecords] = useState<DataMonitoringAlarmRecord[]>([]);
  const [levelCounts, setLevelCounts] = useState<V2DataMonitorAlarmRecordAlarmLevelCountPostResponse>({});
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  const hasDisplayedFieldsList = useHasPermission(PermissionsType.B_SETDISPLAYEDFIELDSLIST);
  const hasDeleteAlarm = useHasPermission(PermissionsType.B_BATCH_DELETE_DATA_MOINTORING_ALERT_RECORDS);
  const levelPermissions = [
    true,
    useHasPermission(PermissionsType.B_SUPERLATIVETAB),
    useHasPermission(PermissionsType.B_ADVANCEDTAB),
    useHasPermission(PermissionsType.B_INTERMEDIATETAB),
    useHasPermission(PermissionsType.B_LOWLEVELTAB),
    useHasPermission(PermissionsType.B_LOWESTLEVELTAB),
  ];

  const levels = level === 5 ? [] : [level];

  useEffect(() => {
    const requestParams: V2DataMonitorAlarmRecordPagePostRequest = {
      ...searchParams,
      page: pageOffset,
      size: pageSize,
      state: Number(tabKey),
      levels,
    };

    setLoading(true);
    apiV2DataMonitorAlarmRecordPagePost(requestParams)
      .then(response => {
        setRecords(response.list ?? []);
        setTotalCount(response.total ?? 0);
      })
      .finally(() => setLoading(false));
    // searchParams 中包含完整筛选条件，reloadKey 用于操作成功后的主动刷新。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, pageOffset, pageSize, reloadKey, searchParams, setTotalCount, tabKey]);

  useEffect(() => {
    apiV2DataMonitorAlarmRecordAlarmLevelCountPost({
      ...searchParams,
      state: Number(tabKey),
      levels: [],
    }).then(setLevelCounts);
  }, [reloadKey, searchParams, tabKey]);

  const ouOptions = useMemo(
    () => ouList.flatMap(item => (item.id === undefined || !item.name ? [] : [{ label: item.name, value: item.id }])),
    [ouList]
  );
  const tagOptions = useMemo(
    () =>
      deviceLabelList.flatMap(item =>
        item.id === undefined || !item.name ? [] : [{ label: item.name, value: item.id }]
      ),
    [deviceLabelList]
  );

  const cascaderFilter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option =>
      String(option.label ?? '')
        .toLowerCase()
        .includes(inputValue.toLowerCase())
    );

  const onFinish = (values: FilterParams) => {
    const currentTime = dayjs();
    const endTime = values.alarmTimeRange?.[1];

    setSearchParams({
      ruleCodeOrName: values.ruleCodeOrName,
      deviceCodeOrName: values.deviceCodeOrName,
      alarmStartTime: values.alarmTimeRange?.[0].valueOf(),
      alarmEndTime: endTime ? (endTime.isAfter(currentTime) ? currentTime : endTime).valueOf() : undefined,
      ouIds: values.ouIds,
      ruleType: values.ruleType,
      deviceTypeIds: getChildNodesByParentIds(values.deviceTypeIds, deviceTypeData?.tree),
      durationFilter: values.durationFilter ? Number(values.durationFilter) : undefined,
      duration: values.duration,
      durationUnit: values.durationUnit ? Number(values.durationUnit) : undefined,
      deviceLabelIds: values.deviceLabelIds,
    });
    setSelectedRowKeys([]);
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams({});
    setSelectedRowKeys([]);
    setPageOffset(1);
  };

  const refresh = () => {
    setSelectedRowKeys([]);
    setReloadKey(value => value + 1);
  };

  const recoverAlarm = (alarmIds: number[]) => {
    Modal.confirm({
      title: '确定解除报警？',
      content: '解除后该报警记录将进入已恢复报警列表。',
      okText: '确定',
      cancelText: '取消',
      onOk: () => apiV2DataMonitorAlarmRecordBatchRelievePost({ ids: alarmIds }).then(refresh),
    });
  };

  const batchDelete = () => {
    Modal.confirm({
      title: '确认批量删除？',
      content: '删除后无法恢复，是否继续？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => apiV2DataMonitorAlarmRecordBatchDeletePost({ ids: selectedRowKeys }).then(refresh),
    });
  };

  const exportRecords = () => {
    window.open(
      `/api/v2/dataMonitorAlarmRecord/export${qs.stringify(
        {
          timestamp: Date.now(),
          ...searchParams,
          state: tabKey,
          levels,
        },
        { addQueryPrefix: true }
      )}`
    );
  };

  const columns = [
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      width: 240,
      fixed: 'left' as const,
      ellipsis: { showTitle: true },
      render: (value?: string, record?: DataMonitoringAlarmRecord) =>
        record?.deviceId ? (
          <Link to={`/device/manage/device/${record.deviceId}/detail`} target="_blank">
            <EllipsisSpan value={value || '--'} />
          </Link>
        ) : (
          <EllipsisSpan value={value || '--'} />
        ),
    },
    {
      title: '报警类型',
      dataIndex: 'ruleType',
      width: 150,
      fixed: 'left' as const,
      render: (_: unknown, record: DataMonitoringAlarmRecord) => {
        const ruleType = record.ruleType;
        return (
          <EllipsisSpan
            value={
              ruleType === undefined ? '--' : DataMonitoringRuleTypeDisplay[ruleType as DataMonitoringRuleType] ?? '--'
            }
          />
        );
      },
    },
    {
      title: '规则名称',
      dataIndex: 'ruleName',
      width: 240,
      fixed: 'left' as const,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '报警等级',
      dataIndex: 'alarmLevel',
      width: 110,
      render: (value?: AlarmLevel) =>
        value === undefined ? (
          '--'
        ) : (
          <Tag border="solid" color={AlarmLevelColorDisplay[value]}>
            {AlarmLevelDisplay[value]}
          </Tag>
        ),
    },
    {
      title: '报警时间',
      dataIndex: 'alarmTime',
      width: 180,
      render: (value?: number) => <EllipsisSpan value={value ? dayjs(value).format(displayDateFormat) : '--'} />,
    },
    {
      title: '恢复时间',
      dataIndex: 'recoveryTime',
      width: 180,
      render: (value?: number) => <EllipsisSpan value={value ? dayjs(value).format(displayDateFormat) : '未恢复'} />,
    },
    {
      title: '报警持续时长',
      dataIndex: 'duration',
      width: 160,
      render: (value?: number | null) => (
        <EllipsisSpan value={value === undefined || value === null ? '--' : `${value.toFixed(2)} min`} />
      ),
    },
    {
      title: '规则编码',
      dataIndex: 'ruleCode',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '设备编码',
      dataIndex: 'deviceCode',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '运营单元',
      dataIndex: 'ouName',
      width: 200,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '所属类目',
      dataIndex: 'deviceTypeName',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '设备标签',
      dataIndex: 'deviceLabels',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string[]) => <EllipsisSpan value={value?.join('、') || '--'} />,
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 100,
      fixed: 'right' as const,
      showForce: true,
      render: (_: unknown, record: DataMonitoringAlarmRecord) => (
        <Dropdown
          type="link"
          menu={{
            items: [
              {
                key: 'detail',
                label: (
                  <span
                    onClick={() =>
                      window.open(`/device/alarm/data-monitoring/detail/${record.id}`, '_blank', 'noopener,noreferrer')
                    }
                  >
                    查看
                  </span>
                ),
              },
              {
                key: 'original-data',
                label: (
                  <span
                    onClick={() => {
                      window.open(
                        `/data/history/device${qs.stringify(
                          {
                            deviceId: record.deviceId,
                            dataPropertyId: record.dataPropertyId,
                            alarmTime: record.alarmTime,
                          },
                          { addQueryPrefix: true }
                        )}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                  >
                    原始数据
                  </span>
                ),
              },
            ],
          }}
        >
          操作项
        </Dropdown>
      ),
    },
  ];

  const filters = (
    <div>
      <Tabs
        activeKey={tabKey}
        items={alarmStateTabs}
        selectedLinePlacement="top"
        onChange={key => {
          setTabKey(key);
          setSelectedRowKeys([]);
          setPageOffset(1);
        }}
      />
      <div style={{ padding: '0 20px' }}>
        <CustomFilter<FilterParams>
          form={form}
          collapseOpen
          onFinish={values => onFinish(values as FilterParams)}
          onReset={onReset}
        >
          <Form.Item name="ruleCodeOrName" label="规则编码/名称">
            <Input allowClear placeholder="请输入规则编码或名称" />
          </Form.Item>
          <Form.Item name="deviceCodeOrName" label="设备编码/名称">
            <Input allowClear placeholder="请输入设备编码或名称" />
          </Form.Item>
          <Form.Item name="alarmTimeRange" label="报警日期">
            <RangePicker allowClear showTime={{ format: 'HH:mm' }} format={dateFormat} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ruleType" label="报警类型">
            <Select allowClear placeholder="请选择报警类型" options={dataMonitoringRuleTypeOptions} />
          </Form.Item>
          <Form.Item name="ouIds" label="运营单元">
            <Select
              allowClear
              mode="multiple"
              showSearch
              optionFilterProp="label"
              maxTagCount={3}
              placeholder="请选择运营单元"
              options={ouOptions}
            />
          </Form.Item>
          <Form.Item name="deviceTypeIds" label="所属类目">
            <Cascader
              allowClear
              multiple
              maxTagCount="responsive"
              options={formatOptionData(deviceTypeData)}
              fieldNames={{ children: 'child' }}
              showSearch={{ filter: cascaderFilter }}
              placeholder="请选择所属类目"
            />
          </Form.Item>
          <Form.Item label="报警持续时长">
            <Space.Compact block>
              <Form.Item name="durationFilter" noStyle>
                <Select allowClear placeholder="条件" style={{ width: 82 }} options={durationConditionOptions} />
              </Form.Item>
              <Form.Item name="duration" noStyle>
                <InputNumber
                  min={0}
                  max={99999999.99}
                  precision={2}
                  placeholder="时长"
                  style={{ width: 'calc(100% - 164px)' }}
                />
              </Form.Item>
              <Form.Item name="durationUnit" noStyle>
                <Select allowClear placeholder="单位" style={{ width: 82 }} options={durationUnitOptions} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="deviceLabelIds" label="设备标签">
            <Select
              allowClear
              mode="multiple"
              showSearch
              optionFilterProp="label"
              maxTagCount={3}
              placeholder="请选择设备标签"
              options={tagOptions}
            />
          </Form.Item>
        </CustomFilter>
      </div>
    </div>
  );

  return (
    <Wrapper
      routes={breadcrumbRoutes?.routes}
      filters={filters}
      className={styles.wrapper}
      filtersStyle={{ padding: 0 }}
      hasPadding={false}
    >
      <div style={{ background: 'var(--mx-module-bg-color)', flex: 1, padding: 20 }}>
        <Space size={8} style={{ width: '100%', marginBottom: 10 }}>
          <Radio.Group
            buttonStyle="solid"
            value={level}
            onChange={event => {
              setLevel(event.target.value);
              setSelectedRowKeys([]);
              setPageOffset(1);
            }}
          >
            {alarmLevelItems.map((item, index) => {
              const count = levelCounts[item.countKey];

              return (
                levelPermissions[index] && (
                  <Radio.Button key={item.value} value={item.value}>
                    {item.label}({typeof count === 'number' ? count : count?.total ?? 0})
                  </Radio.Button>
                )
              );
            })}
          </Radio.Group>
          <Button
            type="primary"
            disabled={tabKey === '0' || selectedRowKeys.length === 0}
            onClick={() => recoverAlarm(selectedRowKeys)}
          >
            解除报警
          </Button>
          {hasDeleteAlarm && (
            <Button type="primary" disabled={selectedRowKeys.length === 0} onClick={batchDelete}>
              批量删除
            </Button>
          )}
          <Button type="primary" disabled={records.length === 0} onClick={exportRecords}>
            导出
          </Button>
        </Space>
        <FieldTable
          rowKey="id"
          sticky={{
            getContainer: () => document.querySelector('.central-menu-content') as HTMLElement,
          }}
          scroll={{ x: 2110 }}
          cacheKey="data_monitoring_alarm_table"
          freezeHide
          adjustField={hasDisplayedFieldsList}
          adjustWidth={hasDisplayedFieldsList}
          loading={loading}
          columns={columns}
          dataSource={records}
          rowSelection={{
            type: 'checkbox',
            fixed: true,
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys.map(Number)),
          }}
        />
        <Paging pagingInfo={pagingInfo} pageSizeOptions={['10', '20', '50', '100', '200']} />
      </div>
    </Wrapper>
  );
};

export default DataMonitoringAlarmRecords;
